/**
 * One manager's year, reduced to a line: regular-season record, points, how it
 * ended. The building block for both a manager's profile and the league-wide
 * best/worst seasons.
 */

import type { HistoryTeam, LeagueHistory } from "../history";
import type { PersonGame, Record3 } from "../reportBuilder/ledger";
import { countsTowardHistory, recordOf } from "../reportBuilder/ledger";
import { round2, winPct } from "../util/math";

export interface ManagerSeasonLine {
  personId: string;
  season: number;
  teamName: string;
  /** Regular season only; playoff and consolation games are left out. */
  record: Record3;
  pointsFor: number;
  pointsAgainst: number;
  /** False for the live season, whose record is still moving. */
  complete: boolean;
  /** Final finish (1 = champion); null while the season is live or when Yahoo gave none. */
  finish: number | null;
  champion: boolean;
  madePlayoffs: boolean;
}

/** Oldest season first. Seasons with no finished games for a manager (e.g. before week 1) produce no line. */
export function buildSeasonLines(history: LeagueHistory, ledger: readonly PersonGame[]): ManagerSeasonLine[] {
  const completeBySeason = new Map<number, boolean>();
  const teamBySeasonPerson = new Map<string, HistoryTeam>();
  for (const season of history.seasons) {
    completeBySeason.set(season.season, season.isFinished);
    for (const team of season.teams) {
      const personId = history.personByTeamKey[team.teamKey];
      // A person with two teams in one season is a flagged data problem; the first team stands in.
      if (personId && !teamBySeasonPerson.has(key(season.season, personId))) {
        teamBySeasonPerson.set(key(season.season, personId), team);
      }
    }
  }

  const gamesBySeasonPerson = new Map<string, PersonGame[]>();
  for (const game of ledger.filter(countsTowardHistory)) {
    const k = key(game.season, game.personId);
    gamesBySeasonPerson.set(k, [...(gamesBySeasonPerson.get(k) ?? []), game]);
  }

  return [...gamesBySeasonPerson.values()].map((games) => {
    const first = games[0]!;
    const regular = games.filter((g) => g.gameType === "regular");
    const complete = completeBySeason.get(first.season) ?? false;
    const rank = teamBySeasonPerson.get(key(first.season, first.personId))?.rank ?? null;
    return {
      personId: first.personId,
      season: first.season,
      teamName: first.teamName,
      record: recordOf(regular),
      pointsFor: round2(sum(regular.map((g) => g.points))),
      pointsAgainst: round2(sum(regular.map((g) => g.opponentPoints))),
      complete,
      finish: complete ? rank : null,
      champion: complete && rank === 1,
      madePlayoffs: games.some((g) => g.gameType === "playoffs"),
    };
  });
}

/** Better first: win percentage, then points scored. */
export function compareSeasonLines(
  a: Pick<ManagerSeasonLine, "record" | "pointsFor">,
  b: Pick<ManagerSeasonLine, "record" | "pointsFor">,
): number {
  return winPct(b.record) - winPct(a.record) || b.pointsFor - a.pointsFor;
}

const key = (season: number, personId: string) => `${season}|${personId}`;
const sum = (values: readonly number[]) => values.reduce((total, v) => total + v, 0);
